// Player move requests and re-confirmation (#2691).
//
// A player never moves a booking: the owner REQUESTS a move that the club
// approves. These are thin transports over the scheduling routes (generated
// types, aliased in @bw/shared/calendar/contract); the server owns the
// cutoff, price, availability and who may ask.
//
//   GET  /facilities/{fid}/reschedule-policy           policy text + cutoff
//   POST /facilities/{fid}/bookings/{bid}/move-quote   owner price check
//   POST /facilities/{fid}/bookings/{bid}/change-requests
//   GET  /bookings/{bid}/change-requests               owner's own requests
//   GET  /bookings/{bid}/reconfirm                     caller's status
//   POST /bookings/{bid}/reconfirm                     confirm / can't make it

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { httpClient } from "@bw/shared/api";
import type {
  BookingMoveQuote,
  ChangeRequestCreate,
  ChangeRequestResponse,
  MoveSlot,
  OwnChangeRequest,
  ReconfirmBody,
  ReconfirmResponse,
  ReschedulePolicy,
} from "@bw/shared/calendar/contract";

export type MoveRequestBody = ChangeRequestCreate;

export const moveRequestKeys = {
  policy: (facilityId: string) => ["booking", "reschedule-policy", facilityId] as const,
  reconfirm: (bookingId: string) => ["booking", "reconfirm", bookingId] as const,
  ownRequests: (bookingId: string) => ["booking", "change-requests", bookingId] as const,
};

export function getReschedulePolicy(facilityId: string): Promise<ReschedulePolicy> {
  return httpClient.get<ReschedulePolicy>(`/facilities/${facilityId}/reschedule-policy`);
}

export function quoteMove(
  facilityId: string,
  bookingId: string,
  slot: MoveSlot,
): Promise<BookingMoveQuote> {
  return httpClient.post<BookingMoveQuote>(
    `/facilities/${facilityId}/bookings/${bookingId}/move-quote`,
    slot,
  );
}

export function requestMove(
  facilityId: string,
  bookingId: string,
  body: MoveRequestBody,
): Promise<ChangeRequestResponse> {
  return httpClient.post<ChangeRequestResponse>(
    `/facilities/${facilityId}/bookings/${bookingId}/change-requests`,
    body,
  );
}

/** The facility's reschedule policy; any signed-in player may read it. */
export function useReschedulePolicy(facilityId: string | undefined) {
  return useQuery({
    queryKey: moveRequestKeys.policy(facilityId ?? ""),
    queryFn: () => getReschedulePolicy(facilityId as string),
    enabled: Boolean(facilityId),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/** Whether `start` is still outside the facility's request cutoff. */
export function outsideCutoff(
  startIso: string,
  cutoffHours: number,
  now = Date.now(),
): boolean {
  const start = Date.parse(startIso);
  return Number.isFinite(start) && start - now >= cutoffHours * 3_600_000;
}

/** The owner's move requests for one booking (403 for anyone else). */
export function useOwnChangeRequests(bookingId: string, enabled: boolean) {
  return useQuery({
    queryKey: moveRequestKeys.ownRequests(bookingId),
    queryFn: () =>
      httpClient
        .get<unknown>(`/bookings/${bookingId}/change-requests`)
        .then((value) => (Array.isArray(value) ? (value as OwnChangeRequest[]) : [])),
    enabled,
    retry: false,
  });
}

/** The caller's re-confirmation state for one booking (403 when not on it). */
export function useReconfirmStatus(bookingId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: moveRequestKeys.reconfirm(bookingId ?? ""),
    queryFn: () =>
      httpClient.get<ReconfirmResponse>(
        `/bookings/${bookingId}/reconfirm`,
      ),
    enabled: Boolean(bookingId) && enabled,
    retry: false,
  });
}

export function useReconfirm(bookingId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: ReconfirmBody) =>
      httpClient.post<ReconfirmResponse>(
        `/bookings/${bookingId}/reconfirm`,
        body,
      ),
    onSuccess: (result) => {
      client.setQueryData(moveRequestKeys.reconfirm(bookingId), result);
    },
  });
}
